/**
 * useDiscoverTabState — combining hook that owns the Discover tab's
 * AI Search state, the "has the initial default search already fired" guard,
 * and the UI's expanded-results toggle.
 *
 * Uses the Brooklyn `aiSearch` pipeline + AI Service `oracleSearchOverviewStream`
 * via `useAiSearchStream`. When no search query is provided, Brooklyn enters
 * discovery mode and resolves the user's topic preferences into a query.
 *
 * Lifted up from `ProfileDiscoverTab` so that the state survives tab
 * switches inside `PlaylistTabContainer` — which stays mounted while the
 * tab children mount/unmount. Users can now leave the Discover tab and
 * return without losing their last search results.
 *
 * Scope: within-page persistence only. Full page navigation still
 * unmounts the parent and resets this hook, which is intentional.
 *
 * Playlist data (playlistItems, userProfile, followerCount) comes directly
 * from the `aiSearchPlaylists` query via the nested `playlist` field —
 * no separate hydration query needed.
 */

import {
  useState, useEffect, useRef, useMemo, useCallback,
} from 'react'
import useAiSearchStream from '../../oracle/useAiSearchStream'
import type { AiSearchStreamState } from '../../oracle/useAiSearchStream'
import { usePostHogEvent } from '../../../../components/PostHog/index'
import type {
  OracleSearchResults,
  OraclePlaylistResult,
} from '../../oracle/useOracleSearch'
import type { PlaylistContentItem } from '../../types'

/** Playlist-only limits: skip content and profile retrieval on the response. */
const DISCOVER_LIMITS = { contentLimit: 0, profileLimit: 0 } as const

/**
 * Module-level cache for the Discover tab's last completed search.
 *
 * Survives unmount of `<CommunityMyLibraryPage>` so a return visit to
 * `/playlist/default` shows the previously fetched playlists immediately
 * and does **not** auto-restart the overview stream — the trigger that
 * would have fired the AI search (Effect A) sees `discoverLoaded === true`
 * and skips.
 *
 * Lifetime: the JS heap.  Cleared on logout / profile switch because
 * both flows call `global.location.assign()` (see auth/actions.js
 * doAuthLogout + changeAuthProfile), which wipes the heap.  An interactive
 * search inside this lifecycle overwrites the cache with fresh values.
 *
 * Why module level: `useAiSearchStream` uses `client.query` with
 * `no-cache`, so Apollo's normalised cache holds nothing for Discover to
 * fall back on; the persistence has to live in the React-hook layer.
 */
type CachedDiscoverState = {
  activeResults: OracleSearchResults
  streamingText: string
  activeError: string | null
  discoverStreamState: AiSearchStreamState
  discoverExpanded: boolean
}

let cachedDiscoverState: CachedDiscoverState | null = null

/** Test-only: reset the module cache between tests. */
export function __resetDiscoverCacheForTests () {
  cachedDiscoverState = null
}

function resolvePlaylistUserProfile (
  profile: { uuid?: string; displayName?: string; avatar?: unknown } | null | undefined,
) {
  // Only surface the creator when we have a real uuid to link to. Without a
  // uuid the row falls through to "by Anonymous" downstream. This also keeps
  // ANONYMOUS / PRIVATE playlists safe — Brooklyn nulls `Playlist.userProfile`
  // for those visibilities, so we never receive a uuid for them here.
  if (!profile?.uuid) return null
  return { uuid: profile.uuid, displayName: profile.displayName, avatar: profile.avatar }
}

export interface UseDiscoverTabStateReturn {
  /** Results currently surfaced to the UI. */
  activeResults: OracleSearchResults
  /** Error message from the stream. */
  activeError: string | null
  /** True when loading (not yet streaming, not yet resolved). */
  activeLoading: boolean
  /** State machine value from the streaming hook. */
  discoverStreamState: AiSearchStreamState
  /** Streaming preamble text rendered above results. */
  streamingText: string
  /** Refine callback (receives the selected refinement text). */
  oracleRefine: (text: string) => void
  /** Re-runs the current searchQuery — used by the "Try again" button on error. */
  retry: () => void
  /** "See more" toggle — lifted so it persists across tab switches. */
  discoverExpanded: boolean
  setDiscoverExpanded: React.Dispatch<React.SetStateAction<boolean>>
}

export default function useDiscoverTabState (
  jwt: string | undefined,
  language: string,
  searchQuery: string,
  searchTrigger: number,
  enabled: boolean = true,
): UseDiscoverTabStateReturn {
  const {
    search: aiSearch,
    refine: aiRefine,
    state: discoverStreamState,
    streamingText,
    results: streamResults,
    error: streamError,
  } = useAiSearchStream(jwt ?? null, language)

  // Seed `discoverLoaded` from the module cache so Effect A's auto-fire
  // is skipped on a return visit — the cached `activeResults` surface
  // below already match what the user saw last time.  A fresh interactive
  // search (Effect B via `searchTrigger`) bypasses this latch.
  const [discoverLoaded, setDiscoverLoaded] = useState<boolean>(
    () => !!cachedDiscoverState,
  )
  const [discoverExpanded, setDiscoverExpanded] = useState<boolean>(
    () => cachedDiscoverState?.discoverExpanded ?? false,
  )

  // Lifecycle flag: has the user fired a search (auto or interactive) on
  // *this* hook instance?  When false on a revisit, we surface the cached
  // values from the previous lifecycle instead of the empty IDLE defaults
  // that `useAiSearchStream` returns before the first call.  Flipped true
  // inside Effect A / Effect B / `retry` / `oracleRefine` below.
  const [hasFiredNewSearch, setHasFiredNewSearch] = useState<boolean>(false)

  // Keep the latest searchQuery available to the retry callback without
  // causing `retry` to be re-created on every keystroke.
  const searchQueryRef = useRef(searchQuery)
  searchQueryRef.current = searchQuery

  // Search-funnel analytics: a `_searched` event per entry-point fire,
  // and a `_search_completed` event on every LOADING → SUCCESS/ERROR transition.
  const trackDiscoverSearched = usePostHogEvent('community_discover_searched')
  const trackDiscoverSearchCompleted = usePostHogEvent('community_discover_search_completed')

  // Effect A — initial fire: run the default (discovery) search the first time
  // the Discover tab becomes enabled (i.e. the user actually visits it).
  // Passing null triggers Brooklyn's discovery mode (user topics as query).
  //
  // Skipped on a revisit when the module cache has results — `discoverLoaded`
  // is seeded `true` from the cache, so the user sees the previously
  // fetched playlists immediately and the overview stream does not
  // auto-restart.
  useEffect(() => {
    if (enabled && !discoverLoaded) {
      trackDiscoverSearched(false)
      aiSearch(searchQuery || null, DISCOVER_LIMITS)
      setDiscoverLoaded(true)
      setHasFiredNewSearch(true)
    }
  }, [enabled, discoverLoaded, aiSearch, searchQuery, trackDiscoverSearched])

  // Effect B — re-fire on explicit search submit. `searchTrigger` is a
  // monotonically increasing counter owned by the parent; comparing with
  // a ref avoids firing on the initial render.
  const searchTriggerRef = useRef(searchTrigger)
  useEffect(() => {
    if (searchTrigger !== searchTriggerRef.current) {
      searchTriggerRef.current = searchTrigger
      trackDiscoverSearched(false)
      aiSearch(searchQuery || null, DISCOVER_LIMITS)
      setHasFiredNewSearch(true)
    }
  }, [searchTrigger, aiSearch, searchQuery, trackDiscoverSearched])

  // Effect C — collapse the expanded list whenever a new search starts.
  useEffect(() => {
    if (discoverStreamState === 'LOADING') {
      setDiscoverExpanded(false)
    }
  }, [discoverStreamState])

  // Effect D — fire community_discover_search_completed when a search resolves
  // (LOADING → RESULTS or LOADING → ERROR). Uses a ref to detect the
  // transition so the initial render doesn't synthesize a completion event.
  const prevStreamStateRef = useRef<AiSearchStreamState>(discoverStreamState)
  useEffect(() => {
    const prev = prevStreamStateRef.current
    prevStreamStateRef.current = discoverStreamState
    if (prev !== 'LOADING') return
    if (discoverStreamState === 'RESULTS') {
      const resultCount = streamResults.playlists.length
      trackDiscoverSearchCompleted(resultCount, resultCount > 0, 'success')
    } else if (discoverStreamState === 'ERROR') {
      trackDiscoverSearchCompleted(0, false, 'error')
    }
  }, [discoverStreamState, streamResults.playlists, trackDiscoverSearchCompleted])

  const retry = useCallback(() => {
    trackDiscoverSearched(false)
    aiSearch(searchQueryRef.current || null, DISCOVER_LIMITS)
    setHasFiredNewSearch(true)
  }, [aiSearch, trackDiscoverSearched])

  const oracleRefine = useCallback((text: string) => {
    trackDiscoverSearched(true)
    aiRefine(text)
    setHasFiredNewSearch(true)
  }, [aiRefine, trackDiscoverSearched])

  // Build OraclePlaylistResult[] directly from the AI search response.
  // The `playlist` field on each result includes playlistItems, userProfile,
  // and followerCount — no separate hydration query needed.
  const playlists = useMemo<OraclePlaylistResult[]>(() => streamResults.playlists.map((tile) => ({
    id: tile.playlistId,
    title: tile.playlistName,
    score: tile.score,
    playlist: tile.playlist ? {
      id: tile.playlist.id,
      playlistName: tile.playlistName,
      description: tile.description ?? null,
      visibility: tile.playlist.visibility,
      itemCount: tile.itemCount ?? undefined,
      followerCount: tile.playlist.followerCount,
      isFollowing: tile.playlist.isFollowing,
      userProfile: resolvePlaylistUserProfile(tile.playlist.userProfile),
      playlistItems: (tile.playlist.playlistItems || []).map((item) => ({
        id: item.id,
        // AiPlaylistTileResult typed contentId as `number | string` (matching the
        // raw GraphQL field), but OraclePlaylistItem.contentId is `string` only.
        // Coerce so downstream consumers don't have to defensively String() it.
        contentId: String(item.contentId),
        contentType: item.contentType,
        playlistPosition: item.playlistPosition,
        // GraphQL types the embedded content array as `unknown[]` because the
        // tile-level alias doesn't fragment Playlist content. Cast at this
        // boundary to PlaylistContentItem[] — the downstream tile builders
        // already shape on these fields.
        content: (item.content as PlaylistContentItem[] | null | undefined) || null,
      })),
    } : {
      id: tile.playlistId,
      playlistName: tile.playlistName,
      description: tile.description ?? null,
      itemCount: tile.itemCount ?? undefined,
      // Without hydrated `tile.playlist`, visibility is unknown — fail
      // closed and refuse the creatorDisplayName fallback. See
      // resolvePlaylistUserProfile for rationale.
      userProfile: null,
      playlistItems: [],
    },
  })), [streamResults.playlists])

  const activeResultsLive = useMemo<OracleSearchResults>(() => ({
    playlists,
    content: streamResults.content,
    profiles: streamResults.profiles,
  }), [playlists, streamResults.content, streamResults.profiles])

  // Surface cached values until a search fires on this lifecycle.  On a
  // cold first visit (cache empty), this falls through to the live empty
  // defaults and Effect A then populates them.  On a revisit, the cache
  // hit means the user sees the previous results and the previous
  // overview text without any new network calls.
  const useCached = !hasFiredNewSearch && cachedDiscoverState != null
  const activeResults = useCached
    ? (cachedDiscoverState as CachedDiscoverState).activeResults
    : activeResultsLive
  const activeError = useCached
    ? (cachedDiscoverState as CachedDiscoverState).activeError
    : streamError
  const effectiveStreamState: AiSearchStreamState = useCached
    ? (cachedDiscoverState as CachedDiscoverState).discoverStreamState
    : discoverStreamState
  const effectiveStreamingText = useCached
    ? (cachedDiscoverState as CachedDiscoverState).streamingText
    : streamingText
  const activeLoading = effectiveStreamState === 'LOADING'

  // Keep latest values in a ref so the cache write below can capture them
  // without re-running on every render.
  const latestRef = useRef<CachedDiscoverState>({
    activeResults,
    streamingText: effectiveStreamingText,
    activeError,
    discoverStreamState: effectiveStreamState,
    discoverExpanded,
  })
  latestRef.current = {
    activeResults,
    streamingText: effectiveStreamingText,
    activeError,
    discoverStreamState: effectiveStreamState,
    discoverExpanded,
  }

  // Persist into the module cache when a search resolves (LOADING →
  // RESULTS / ERROR) so the next visit can paint from it.  Also persist
  // on unmount as a catch-all (e.g. user navigates away mid-stream from
  // RESULTS already shown).
  useEffect(() => {
    if (!hasFiredNewSearch) return
    if (discoverStreamState === 'RESULTS' || discoverStreamState === 'ERROR') {
      cachedDiscoverState = { ...latestRef.current }
    }
  }, [discoverStreamState, hasFiredNewSearch])

  useEffect(() => () => {
    // On unmount, flush the latest snapshot.  Avoids losing the most
    // recent `discoverExpanded` toggle or a refinement that landed just
    // before navigation.
    cachedDiscoverState = { ...latestRef.current }
  }, [])

  return {
    activeResults,
    activeError,
    activeLoading,
    discoverStreamState: effectiveStreamState,
    streamingText: effectiveStreamingText,
    oracleRefine,
    retry,
    discoverExpanded,
    setDiscoverExpanded,
  }
}
